/**
 * شاشة «الإشعارات» — درج الجرس (design/Notifications.dc.html).
 *
 * 🔒 قرار مقفول: **الدرج بلا أزرار فعل.** الضغط على أي إشعار بيوديك للمهمة
 *    نفسها في «مكتبي» — عشان ما يبقاش فيه قائمتين مهام والموظف يشتغل من
 *    الاتنين ويسيب حاجة ورا. النص ده مكتوب تحت الدرج للموظف نفسه.
 *
 * الشكل: درج على اليمين (شرايح · صفوف · ترقيم) + عمود جانبي فيه شكل رسالة
 * تليجرام وإعدادات إشعاراتي وربط تليجرام.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Shell } from "@/components/Shell";
import { Icon, type IconName } from "@/components/Icon";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  OfflineState,
} from "@/components/states/ScreenStates";
import { OfflineError, errorMessageAr } from "@/api/client";
import {
  createTelegramLink,
  deepLinkPath,
  getPreferences,
  listNotifications,
  markAllRead,
  markRead,
  unlinkTelegram,
  updatePreferences,
  type NotificationPrefs,
  type NotificationRow,
  type NotificationTab,
  type NotificationsPage,
  type PrefToggleKey,
  type TelegramLink,
} from "@/api/notifications";
import { Ltr, fmtNum, fmtRelative } from "@/lib/format";
import { useRouter } from "@/lib/router";
import { useSession } from "@/lib/session";

/** الدرج بيعرض ٥ في الصفحة زي التصميم — «عرض 1–5 من 12» */
const PER_PAGE = 5;

const TABS: readonly { key: NotificationTab; label: string }[] = [
  { key: "all", label: "الكل" },
  { key: "mine", label: "يخصني" },
  { key: "team", label: "فريقي" },
];

const PREF_ROWS: readonly { key: PrefToggleKey; label: string }[] = [
  { key: "handoff", label: "تسليم وصلني" },
  { key: "sla_warning", label: "مهمة قربت تتأخر" },
  { key: "announcements", label: "إعلانات الإدارة" },
  { key: "daily_digest", label: "ملخص آخر اليوم" },
];

type DrawerState =
  | { kind: "loading" }
  | { kind: "ready"; page: NotificationsPage }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

type PrefsState =
  | { kind: "loading" }
  | { kind: "ready"; prefs: NotificationPrefs }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

type LinkState =
  | { kind: "idle" }
  | { kind: "busy" }
  | { kind: "ready"; link: TelegramLink };

type Flash = { tone: "ok" | "bad"; node: ReactNode };

export function Notifications() {
  const { navigate } = useRouter();
  const { reload } = useSession();

  const [tab, setTab] = useState<NotificationTab>("all");
  const [page, setPage] = useState(1);
  /** بيزيد عشان نعيد الجلب بعد أي فعل — بدل ما نعيد تحميل الصفحة كلها */
  const [nonce, setNonce] = useState(0);

  const [state, setState] = useState<DrawerState>({ kind: "loading" });
  const [prefsState, setPrefsState] = useState<PrefsState>({ kind: "loading" });

  const [openingId, setOpeningId] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [busyPref, setBusyPref] = useState<PrefToggleKey | null>(null);
  const [linkState, setLinkState] = useState<LinkState>({ kind: "idle" });
  const [unlinking, setUnlinking] = useState(false);

  const [flash, setFlash] = useState<Flash | null>(null);
  const [prefFlash, setPrefFlash] = useState<Flash | null>(null);

  /* ───────────── جلب الدرج ───────────── */

  useEffect(() => {
    const ctrl = new AbortController();
    let alive = true;
    setState({ kind: "loading" });
    listNotifications({ tab, page, perPage: PER_PAGE, withCounts: true, signal: ctrl.signal })
      .then((res) => {
        if (alive) setState({ kind: "ready", page: res });
      })
      .catch((err: unknown) => {
        if (!alive || (err as Error)?.name === "AbortError") return;
        // ⛔ ممنوع نبلع الخطأ — الحالة بتبان للموظف ومعاها سبب السيستم
        setState(
          err instanceof OfflineError
            ? { kind: "offline" }
            : { kind: "error", messageAr: errorMessageAr(err) },
        );
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [tab, page, nonce]);

  /* ───────────── جلب التفضيلات ───────────── */

  const loadPrefs = useCallback((signal?: AbortSignal) => {
    setPrefsState({ kind: "loading" });
    getPreferences(signal)
      .then((prefs) => setPrefsState({ kind: "ready", prefs }))
      .catch((err: unknown) => {
        if ((err as Error)?.name === "AbortError") return;
        setPrefsState(
          err instanceof OfflineError
            ? { kind: "offline" }
            : { kind: "error", messageAr: errorMessageAr(err) },
        );
      });
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    loadPrefs(ctrl.signal);
    return () => ctrl.abort();
  }, [loadPrefs]);

  /* ───────────── الأفعال ───────────── */

  /**
   * فتح الإشعار = الرواح للمهمة نفسها.
   * ⚠️ لو التعليم كمقروء فشل، بنقف مكاننا ونقول السبب — لأن الرسالة
   *    هتضيع لو رحنا لشاشة تانية، والموظف كان هيفتكر إنها اتقرت وهي لأ.
   */
  async function openNotification(row: NotificationRow) {
    if (openingId) return; // قفل ضد الدوس المزدوج
    const target = deepLinkPath(row.deep_link);
    if (row.read_at) {
      navigate(target);
      return;
    }
    setOpeningId(row.id);
    setFlash(null);
    try {
      await markRead(row.id);
      void reload(); // شارة الجرس في الشريط العلوي بتتحدّث من `/auth/me`
      navigate(target);
    } catch (err) {
      setFlash({
        tone: "bad",
        node: <>مقدرناش نعلّم الإشعار كمقروء — {errorMessageAr(err)}</>,
      });
    } finally {
      setOpeningId(null);
    }
  }

  async function onMarkAll() {
    if (markingAll) return;
    setMarkingAll(true);
    setFlash(null);
    try {
      const marked = await markAllRead(tab);
      setFlash(
        marked > 0
          ? {
              tone: "ok",
              node: (
                <>
                  اتعلّم <span className="num">{fmtNum(marked)}</span> إشعار كمقروء
                </>
              ),
            }
          : { tone: "ok", node: <>مفيش إشعارات غير مقروءة في الشريحة دي</> },
      );
      setNonce((n) => n + 1);
      void reload();
    } catch (err) {
      setFlash({ tone: "bad", node: errorMessageAr(err) });
    } finally {
      setMarkingAll(false);
    }
  }

  async function onTogglePref(key: PrefToggleKey, label: string) {
    if (prefsState.kind !== "ready" || busyPref) return;
    const before = prefsState.prefs;
    const next = !before[key];

    // تحديث تفاؤلي — وبيترجع صريح لو السيستم رفض
    const optimistic: NotificationPrefs = { ...before };
    optimistic[key] = next;
    setPrefsState({ kind: "ready", prefs: optimistic });
    setBusyPref(key);
    setPrefFlash(null);

    const patch: Partial<Record<PrefToggleKey, boolean>> = {};
    patch[key] = next;

    try {
      const fresh = await updatePreferences(patch);
      setPrefsState({ kind: "ready", prefs: fresh });
      setPrefFlash({ tone: "ok", node: <>«{label}» {next ? "اتفتح" : "اتقفل"}</> });
    } catch (err) {
      setPrefsState({ kind: "ready", prefs: before }); // رجوع للحالة الحقيقية
      setPrefFlash({ tone: "bad", node: errorMessageAr(err) });
    } finally {
      setBusyPref(null);
    }
  }

  async function onCreateLink() {
    if (linkState.kind === "busy") return;
    setLinkState({ kind: "busy" });
    setPrefFlash(null);
    try {
      const link = await createTelegramLink();
      setLinkState({ kind: "ready", link });
    } catch (err) {
      setLinkState({ kind: "idle" });
      setPrefFlash({ tone: "bad", node: errorMessageAr(err) });
    }
  }

  async function onUnlink() {
    if (unlinking) return;
    setUnlinking(true);
    setPrefFlash(null);
    try {
      const message = await unlinkTelegram();
      setLinkState({ kind: "idle" });
      setPrefFlash({ tone: "ok", node: message });
      loadPrefs();
    } catch (err) {
      setPrefFlash({ tone: "bad", node: errorMessageAr(err) });
    } finally {
      setUnlinking(false);
    }
  }

  /* ───────────── مشتقات العرض ───────────── */

  const rows = state.kind === "ready" ? state.page.rows : [];
  const meta = state.kind === "ready" ? state.page.meta : null;
  const counts = state.kind === "ready" ? state.page.counts : null;

  const total = meta?.total ?? rows.length;
  const totalPages = Math.max(1, meta?.total_pages ?? 1);
  const firstIndex = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastIndex = (page - 1) * PER_PAGE + rows.length;

  function countFor(key: NotificationTab): number | null {
    if (!counts) return null;
    return key === "all" ? counts.all : key === "mine" ? counts.mine : counts.team;
  }

  const prefs = prefsState.kind === "ready" ? prefsState.prefs : null;
  const linked = prefs?.telegram_linked ?? false;

  return (
    <Shell
      title="الإشعارات"
      crumb="كل حاجة حصلت في السيستم — والفعل نفسه بيتم من مكتبي"
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
          minWidth: 0,
        }}
      >
        {/* ═══════════ (1) درج الجرس ═══════════ */}
        <div
          style={{
            flex: "0 1 420px",
            minWidth: 300,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div className="card" style={{ boxShadow: "var(--sh-lift)", overflow: "hidden" }}>
            <div
              style={{
                padding: "18px 20px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div className="sec-h">
                <span className="sec-title">الإشعارات</span>
                <button
                  type="button"
                  className="link"
                  style={{ marginRight: "auto" }}
                  disabled={markingAll || state.kind !== "ready"}
                  onClick={() => void onMarkAll()}
                >
                  {markingAll ? "بيتعلّم…" : "تعليم الكل كمقروء"}
                </button>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {TABS.map((t) => {
                  const n = countFor(t.key);
                  return (
                    <button
                      key={t.key}
                      type="button"
                      className={`filt${tab === t.key ? " on" : ""}`}
                      aria-pressed={tab === t.key}
                      onClick={() => {
                        setTab(t.key);
                        setPage(1);
                      }}
                    >
                      {t.label}
                      {n === null ? null : <span className="num">{fmtNum(n)}</span>}
                    </button>
                  );
                })}
              </div>

              {flash ? <FlashLine flash={flash} /> : null}
            </div>

            {/* حالات الشاشة — ⛔ ممنوع درج فاضي بلا حالة */}
            {state.kind === "loading" ? (
              <div style={{ padding: "0 20px 18px" }}>
                <LoadingState rows={4} />
              </div>
            ) : null}

            {state.kind === "offline" ? (
              <div style={{ padding: "0 20px 18px" }}>
                <OfflineState onRetry={() => setNonce((n) => n + 1)} />
              </div>
            ) : null}

            {state.kind === "error" ? (
              <div style={{ padding: "0 20px 18px" }}>
                <ErrorState
                  messageAr={state.messageAr}
                  onRetry={() => setNonce((n) => n + 1)}
                />
              </div>
            ) : null}

            {state.kind === "ready" && rows.length === 0 ? (
              <div style={{ padding: "0 20px 18px" }}>
                <EmptyState
                  title="مفيش إشعارات في الشريحة دي"
                  hint={
                    tab === "team"
                      ? "لما حاجة تحصل في فريقك هتلاقيها هنا"
                      : "خلّصت كل حاجة — أي جديد هيظهر هنا"
                  }
                />
              </div>
            ) : null}

            {rows.map((row) => (
              <NotificationLine
                key={row.id}
                row={row}
                busy={openingId === row.id}
                disabled={openingId !== null && openingId !== row.id}
                onOpen={() => void openNotification(row)}
              />
            ))}

            {state.kind === "ready" && rows.length > 0 ? (
              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid var(--line)",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <span className="t-meta">
                  عرض <span className="num">{fmtNum(firstIndex)}</span>–
                  <span className="num">{fmtNum(lastIndex)}</span> من{" "}
                  <span className="num">{fmtNum(total)}</span>
                </span>
                <div style={{ marginRight: "auto", display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    className="btn sm ghost"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    السابق
                  </button>
                  <button
                    type="button"
                    className="btn sm ghost"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    التالي
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {/* النص ده هو القرار نفسه مكتوب للموظف */}
          <div className="t-meta" style={{ lineHeight: 1.7 }}>
            الضغط على أي إشعار بيوديك للمهمة نفسها في مكتبي — الإشعارات ما بتتعملش من هنا
          </div>
          <div className="t-meta" style={{ lineHeight: 1.7 }}>
            الدرج بيعرض آخر <span className="num">{fmtNum(30)}</span> يوم
          </div>
        </div>

        {/* ═══════════ (2) العمود الجانبي ═══════════ */}
        <div
          style={{
            flex: "1 1 340px",
            maxWidth: 400,
            minWidth: 300,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <TelegramShapeCard />

          <div
            className="card"
            style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}
          >
            <div className="sec-h">
              <span className="sec-title">إعدادات إشعاراتي</span>
            </div>

            {prefsState.kind === "loading" ? <LoadingState rows={4} /> : null}
            {prefsState.kind === "offline" ? <OfflineState onRetry={() => loadPrefs()} /> : null}
            {prefsState.kind === "error" ? (
              <ErrorState messageAr={prefsState.messageAr} onRetry={() => loadPrefs()} />
            ) : null}

            {prefs ? (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {PREF_ROWS.map((row) => (
                    <PrefToggle
                      key={row.key}
                      label={row.label}
                      on={prefs[row.key]}
                      busy={busyPref === row.key}
                      disabled={busyPref !== null && busyPref !== row.key}
                      onToggle={() => void onTogglePref(row.key, row.label)}
                    />
                  ))}
                </div>

                {prefFlash ? <FlashLine flash={prefFlash} /> : null}

                {prefs.notice_ar ? (
                  <div
                    className="t-meta"
                    style={{
                      borderTop: "1px solid var(--line)",
                      paddingTop: 12,
                      lineHeight: 1.7,
                    }}
                  >
                    {prefs.notice_ar}
                  </div>
                ) : null}

                {/* ───── ربط تليجرام ───── */}
                <div
                  style={{
                    borderTop: "1px solid var(--line)",
                    paddingTop: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div
                    className="t-desc"
                    style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
                  >
                    <span>ربط تليجرام:</span>
                    <span className={`chip ${linked ? "okc" : "soon"}`}>
                      <span className="bullet" />
                      {linked ? "متصل" : "مش متصل"}
                    </span>
                    <span>— بيتعمل مرة واحدة عند أول تشغيل</span>
                  </div>

                  {linked ? (
                    <button
                      type="button"
                      className="btn sm ghost"
                      style={{ alignSelf: "flex-start" }}
                      disabled={unlinking}
                      onClick={() => void onUnlink()}
                    >
                      {unlinking ? "بيتفصل…" : "فصل الربط"}
                    </button>
                  ) : linkState.kind === "ready" ? (
                    <TelegramLinkPanel link={linkState.link} />
                  ) : (
                    <button
                      type="button"
                      className="btn sm primary"
                      style={{ alignSelf: "flex-start" }}
                      disabled={linkState.kind === "busy"}
                      onClick={() => void onCreateLink()}
                    >
                      {linkState.kind === "busy" ? "بيجهّز…" : "اربط تليجرام"}
                    </button>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </Shell>
  );
}

/* ══════════════════════════════════════════════════════════════════
   صف الإشعار
   ══════════════════════════════════════════════════════════════════ */

/** لون واحد لكل نوع — نفس منطق «لكل حالة لون واحد» */
type Tone = "info" | "danger" | "warn" | "auto";

const TONE_BG: Record<Tone, string> = {
  info: "var(--blue-50)",
  danger: "var(--danger-bg)",
  warn: "var(--warn-bg)",
  auto: "var(--auto-bg)",
};

/** ⚠️ هيكس صريح مش متغيّر CSS — `stroke` كخاصية عرض ما بتفهمش `var()` */
const TONE_STROKE: Record<Tone, string> = {
  info: "#1A2B4A",
  danger: "#B42318",
  warn: "#B54708",
  auto: "#475467",
};

function toneOf(row: NotificationRow): Tone {
  if (row.urgency === "urgent") return "danger";
  switch (row.type) {
    case "handoff":
      return "info";
    case "sla_warning":
      return "danger";
    case "event":
      return "auto";
    case "decision_needed":
    case "system":
      return "warn";
    default:
      return "auto";
  }
}

function iconOf(row: NotificationRow): IconName {
  switch (row.type) {
    case "handoff":
      return "logout";
    case "sla_warning":
      return "circle-alert";
    case "event":
      return "bolt";
    case "decision_needed":
      return "eval";
    case "system":
      return "file";
    default:
      return "info";
  }
}

function NotificationLine({
  row,
  busy,
  disabled,
  onOpen,
}: {
  row: NotificationRow;
  busy: boolean;
  disabled: boolean;
  onOpen: () => void;
}) {
  const tone = toneOf(row);
  const unread = row.read_at === null;
  const when = row.last_occurred_at ? fmtRelative(row.last_occurred_at) : null;

  return (
    <button
      type="button"
      className="row-link"
      onClick={onOpen}
      disabled={busy || disabled}
      aria-label={`${unread ? "غير مقروء" : "مقروء"} — ${row.title_ar} — فتح المهمة في مكتبي`}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "12px 20px",
        borderTop: "1px solid var(--line)",
        borderInline: 0,
        borderBottom: 0,
        width: "100%",
        background: unread ? "var(--surface)" : "transparent",
        textAlign: "start",
        font: "inherit",
        cursor: busy || disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: TONE_BG[tone],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon name={iconOf(row)} size={16} stroke={TONE_STROKE[tone]} />
      </span>

      <span
        style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: 1 }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          {/* غير المقروء بنقطة كحلية — الفرق لازم يبان من غير ما نقرا الألوان بس */}
          {unread ? (
            <span
              aria-hidden="true"
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "var(--blue)",
                flexShrink: 0,
              }}
            />
          ) : null}
          <span
            className="t-body clip"
            style={{
              color: unread ? "var(--ink)" : "var(--mut)",
              fontWeight: unread ? 600 : 400,
            }}
          >
            {row.title_ar}
          </span>
          {row.rollup_count > 1 ? (
            <span className="chip auto" style={{ flexShrink: 0 }}>
              <span className="bullet" />
              <span className="num">{fmtNum(row.rollup_count)}</span>
            </span>
          ) : null}
        </span>

        <span className="t-meta">
          {row.origin_label_ar ?? "السيستم"}
          {when ? ` · ${when}` : null}
          {busy ? " · بيفتح…" : null}
        </span>
      </span>
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════════
   العمود الجانبي
   ══════════════════════════════════════════════════════════════════ */

/** مثال ثابت بيوضّح شكل الرسالة اللي بتوصل على الخاص — مش داتا حيّة */
function TelegramShapeCard() {
  return (
    <div
      className="card"
      style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div className="sec-h">
        <span className="sec-title">شكل الرسالة على تليجرام</span>
        <span className="sec-note" style={{ marginRight: "auto" }}>
          مثال
        </span>
      </div>
      <div>
        <div
          style={{
            background: "var(--blue-50)",
            borderRadius: "14px 4px 14px 14px",
            padding: "14px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--blue-900)" }}>
            رتّل — تسليم جديد
          </div>
          <div className="t-desc">وصلك تسليم من فريق التقييم</div>
          <div className="t-body">أ. مشرفة التقييم ب قبلت ترشيح أسرة أبو زيد</div>
          <div className="t-desc">خطوتك: إنشاء الجروب ورسالة التعريف</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--blue)" }}>
            افتح المهمة في السيستم ←
          </div>
        </div>
        <div className="t-meta" style={{ padding: "8px 4px 0" }}>
          <Ltr>4:12</Ltr> م
        </div>
      </div>
      <div
        className="t-desc"
        style={{ borderTop: "1px solid var(--line)", paddingTop: 12, lineHeight: 1.7 }}
      >
        بتوصل لكل واحد في الخاص من بوت الأكاديمية — والعاجل بس
      </div>
    </div>
  );
}

function PrefToggle({
  label,
  on,
  busy,
  disabled,
  onToggle,
}: {
  label: string;
  on: boolean;
  busy: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={busy || disabled}
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        background: "none",
        border: 0,
        padding: 0,
        font: "inherit",
        textAlign: "start",
        cursor: busy || disabled ? "not-allowed" : "pointer",
        opacity: busy || disabled ? 0.6 : 1,
      }}
    >
      <span className="t-body">{label}</span>
      {busy ? <span className="t-meta">بيتحفظ…</span> : null}
      <span
        aria-hidden="true"
        style={{
          marginRight: "auto",
          width: 34,
          height: 20,
          borderRadius: 999,
          background: on ? "var(--blue)" : "var(--line)",
          display: "flex",
          alignItems: "center",
          justifyContent: on ? "flex-start" : "flex-end",
          padding: "0 2px",
          flexShrink: 0,
        }}
      >
        <i
          style={{
            display: "block",
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: "#FFFFFF",
            boxShadow: on ? "none" : "var(--sh)",
          }}
        />
      </span>
    </button>
  );
}

/** بيظهر بعد ما الكود يتجهّز — الرابط والكود البديل ومهلة الصلاحية */
function TelegramLinkPanel({ link }: { link: TelegramLink }) {
  return (
    <div
      style={{
        background: "var(--blue-50)",
        borderRadius: "var(--r-control)",
        padding: 14,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div className="t-body">افتح بوت الأكاديمية واضغط ابدأ</div>
      <a
        className="btn sm ghost"
        style={{ alignSelf: "flex-start", textDecoration: "none" }}
        href={link.deep_link}
        target="_blank"
        rel="noreferrer noopener"
      >
        فتح بوت الأكاديمية
      </a>
      <div className="t-desc">
        ولو الرابط ما فتحش، ابعت الكود ده:{" "}
        <Ltr>{link.manual_code}</Ltr>
      </div>
      <div className="t-meta">الكود بيقع بعد شوية — لو وقع اطلب واحد جديد</div>
    </div>
  );
}

/** سطر نتيجة فعل — نجاح أو فشل، وبيبان دايمًا (⛔ مفيش فشل صامت) */
function FlashLine({ flash }: { flash: Flash }) {
  const bad = flash.tone === "bad";
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        borderRadius: "var(--r-control)",
        padding: "8px 12px",
        background: bad ? "var(--danger-bg)" : "var(--ok-bg)",
        color: bad ? "var(--danger)" : "var(--ok)",
        fontSize: "var(--fs-sm)",
        fontWeight: 500,
        lineHeight: 1.6,
      }}
    >
      <Icon
        name={bad ? "circle-alert" : "check"}
        size={16}
        stroke={bad ? "#B42318" : "#067647"}
      />
      <span style={{ minWidth: 0 }}>{flash.node}</span>
    </div>
  );
}
